"use client"

import { memo, useCallback, useMemo, useState } from "react"
import {
  ArrowLeft,
  Check,
  ExternalLink,
  Loader2,
  ShieldCheck,
  Smartphone,
} from "lucide-react"

import { cn } from "@shared/ui/lib/utils"
import { Button } from "@shared/ui/components/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/components/dialog"

import walletConnectLogo from "../assets/wallets/walletconnect.svg"
import { walletChains, type WalletChainId } from "../config"
import { useWalletChain } from "../context/wallet-context"
import {
  featuredWallets,
  getSupportedConnectChains,
  useFeaturedWalletConnect,
} from "../wallet"
import type { FeaturedWallet } from "../wallet/types"
import { WalletIcon } from "./wallet-icon"

const MOBILE_UA =
  typeof navigator !== "undefined" &&
  /android|iphone|ipad|ipod|harmony/i.test(navigator.userAgent)

type WalletTileProps = {
  wallet: FeaturedWallet
  pendingId: string | null
  detected: boolean
  mobile: boolean
  labels: {
    detected: string
    openApp: string
    install: string
    rejected: string
  }
  onSelect: (wallet: FeaturedWallet) => void
}

const WalletTile = memo(function WalletTile({
  wallet,
  pendingId,
  detected,
  mobile,
  labels,
  onSelect,
}: WalletTileProps) {
  const isPending = pendingId === wallet.id
  const status = detected
    ? { icon: <Check className="size-3 shrink-0" />, text: labels.detected, className: "text-emerald-400" }
    : mobile
      ? { icon: <Smartphone className="size-3 shrink-0" />, text: labels.openApp, className: "text-muted-foreground" }
      : { icon: <ExternalLink className="size-3 shrink-0" />, text: labels.install, className: "text-muted-foreground" }

  return (
    <button
      type="button"
      disabled={Boolean(pendingId)}
      onClick={() => onSelect(wallet)}
      className={cn(
        "flex min-w-0 flex-col items-center gap-1.5 rounded-xl border border-white/10 bg-white/5 p-2 text-center transition-colors",
        "hover:border-primary/40 hover:bg-white/10 disabled:opacity-60",
        isPending && "border-primary/50",
      )}
    >
      <span
        className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white/90 p-1 shadow-inner"
        aria-hidden="true"
      >
        {isPending ? (
          <Loader2 className="size-5 animate-spin text-primary" />
        ) : (
          <WalletIcon
            logo={wallet.logo}
            name={wallet.name}
            className="size-full object-contain"
          />
        )}
      </span>
      <span className="w-full min-w-0">
        <span className="block truncate text-[11px] font-medium leading-tight">
          {wallet.name}
        </span>
        <span
          className={cn(
            "mt-0.5 flex items-center justify-center gap-0.5 text-[9px] leading-none",
            status.className,
          )}
        >
          {status.icon}
          <span className="truncate">{status.text}</span>
        </span>
      </span>
    </button>
  )
})

type ChainTileProps = {
  chainId: WalletChainId
  pending: boolean
  disabled: boolean
  onSelect: (chainId: WalletChainId) => void
}

const ChainTile = memo(function ChainTile({
  chainId,
  pending,
  disabled,
  onSelect,
}: ChainTileProps) {
  const chain = walletChains[chainId]

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onSelect(chainId)}
      className={cn(
        "flex w-full items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-left transition-colors",
        "hover:border-primary/40 hover:bg-white/10 disabled:opacity-60",
        pending && "border-primary/50",
      )}
    >
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium">{chain.label}</span>
        <span className="mt-0.5 block text-xs text-muted-foreground">
          {chain.symbol}
        </span>
      </span>
      {pending ? (
        <Loader2 className="size-4 shrink-0 animate-spin text-primary" />
      ) : null}
    </button>
  )
})

export function ConnectWalletModal() {
  const { modalOpen, modalLabels, closeWalletModal } = useWalletChain()
  const { connectFeatured, openOtherWallets, walletDetectedMap } =
    useFeaturedWalletConnect()
  const [step, setStep] = useState<"wallets" | "chains">("wallets")
  const [selectedWallet, setSelectedWallet] = useState<FeaturedWallet | null>(
    null,
  )
  const [pendingId, setPendingId] = useState<string | null>(null)
  const [pendingChainId, setPendingChainId] = useState<WalletChainId | null>(
    null,
  )
  const [error, setError] = useState<string | null>(null)

  const tileLabels = useMemo(
    () => ({
      detected: modalLabels.detected,
      openApp: modalLabels.openApp,
      install: modalLabels.install,
      rejected: modalLabels.rejected,
    }),
    [
      modalLabels.detected,
      modalLabels.install,
      modalLabels.openApp,
      modalLabels.rejected,
    ],
  )

  const resetModalState = useCallback(() => {
    setStep("wallets")
    setSelectedWallet(null)
    setPendingId(null)
    setPendingChainId(null)
    setError(null)
  }, [])

  const handleConnect = useCallback(
    async (wallet: FeaturedWallet, chainId: WalletChainId) => {
      setError(null)
      setPendingId(wallet.id)
      setPendingChainId(chainId)
      try {
        await connectFeatured(wallet, chainId)
      } catch (cause) {
        const message =
          cause instanceof Error ? cause.message : modalLabels.rejected
        if (!message.includes("Redirecting")) {
          setError(
            message === "Connection cancelled" ? modalLabels.rejected : message,
          )
        }
      } finally {
        setPendingId(null)
        setPendingChainId(null)
      }
    },
    [connectFeatured, modalLabels.rejected],
  )

  const handleSelectWallet = useCallback(
    (wallet: FeaturedWallet) => {
      setError(null)
      const chains = getSupportedConnectChains(wallet)
      if (chains.length === 1) {
        void handleConnect(wallet, chains[0]!)
        return
      }
      setSelectedWallet(wallet)
      setStep("chains")
    },
    [handleConnect],
  )

  const handleSelectChain = useCallback(
    (chainId: WalletChainId) => {
      if (!selectedWallet) return
      void handleConnect(selectedWallet, chainId)
    },
    [handleConnect, selectedWallet],
  )

  const handleBack = useCallback(() => {
    setStep("wallets")
    setSelectedWallet(null)
    setError(null)
  }, [])

  const supportedChains = selectedWallet
    ? getSupportedConnectChains(selectedWallet)
    : []

  return (
    <Dialog
      open={modalOpen}
      onOpenChange={(open) => {
        if (!open) {
          resetModalState()
          closeWalletModal()
        }
      }}
    >
      <DialogContent
        className={cn(
          "max-w-[24rem] gap-0 border border-white/10 bg-popover p-0 sm:max-w-[24rem]",
          "data-open:animate-none data-closed:animate-none",
        )}
      >
        <div className="flex flex-col gap-4 p-6">
          {step === "chains" && selectedWallet ? (
            <>
              <DialogHeader className="text-center sm:text-left">
                <div className="mb-1 flex items-center gap-2">
                  <button
                    type="button"
                    disabled={Boolean(pendingId)}
                    onClick={handleBack}
                    className="inline-flex size-8 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground disabled:opacity-60"
                    aria-label={modalLabels.back}
                  >
                    <ArrowLeft className="size-4" />
                  </button>
                  <div className="min-w-0 flex-1">
                    <DialogTitle>{modalLabels.selectChainTitle}</DialogTitle>
                    <DialogDescription>
                      {modalLabels.selectChainSubtitle}
                    </DialogDescription>
                  </div>
                </div>
                <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2">
                  <span className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-md bg-white/90 p-1">
                    <WalletIcon
                      logo={selectedWallet.logo}
                      name={selectedWallet.name}
                      className="size-full object-contain"
                    />
                  </span>
                  <span className="truncate text-sm font-medium">
                    {selectedWallet.name}
                  </span>
                </div>
              </DialogHeader>

              <div className="flex flex-col gap-2">
                {supportedChains.map((chainId) => (
                  <ChainTile
                    key={chainId}
                    chainId={chainId}
                    pending={pendingChainId === chainId}
                    disabled={Boolean(pendingId)}
                    onSelect={handleSelectChain}
                  />
                ))}
              </div>
            </>
          ) : (
            <>
              <DialogHeader className="text-center sm:text-left">
                <DialogTitle>{modalLabels.title}</DialogTitle>
                <DialogDescription>{modalLabels.subtitle}</DialogDescription>
              </DialogHeader>

              <div
                className="grid grid-cols-4 gap-2"
                style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))" }}
              >
                {featuredWallets.map((wallet) => (
                  <WalletTile
                    key={wallet.id}
                    wallet={wallet}
                    pendingId={pendingId}
                    detected={walletDetectedMap[wallet.id] ?? false}
                    mobile={MOBILE_UA}
                    labels={tileLabels}
                    onSelect={handleSelectWallet}
                  />
                ))}
              </div>

              <div className="border-t border-white/10 pt-3">
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 w-full gap-2 border-white/15 bg-white/5"
                  disabled={Boolean(pendingId)}
                  onClick={() => {
                    setError(null)
                    setPendingId("other")
                    void openOtherWallets()
                      .catch(() => setError(modalLabels.rejected))
                      .finally(() => setPendingId(null))
                  }}
                >
                  {pendingId === "other" ? (
                    <Loader2 className="size-4 shrink-0 animate-spin" />
                  ) : (
                    <WalletIcon
                      logo={walletConnectLogo}
                      name="WalletConnect"
                      className="size-5 shrink-0 object-contain"
                    />
                  )}
                  <span>{modalLabels.otherWallets}</span>
                </Button>
              </div>
            </>
          )}

          {error ? (
            <p className="text-center text-xs text-red-400">{error}</p>
          ) : null}

          <p className="flex items-center justify-center gap-1.5 text-center text-xs text-muted-foreground">
            <ShieldCheck className="size-3.5 shrink-0 text-emerald-400" />
            {modalLabels.secure}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  )
}
